<template>
  <figure 
    role="group"
    class="vcl-histogram"
    :style="chartStyles"
  >
    <vp-chart-legends 
      v-if="showLegend && hasData"
      :chart-instance="chartInstance"
      :chart-data="chartData"
      :styles="legendStyles"
    >
      <template #legend="{ legend }">
        <slot name="legend" :legend="legend" />
      </template>
    </vp-chart-legends>
    <Bar
      v-if="hasData"
      ref="histogramRef" 
      class="vcl-histogram__graph"
      :chart-id="chartId" 
      :chart-data="chartData"
      :chart-options="chartOptions"
      @hook:mounted="setChartInstance" 
    />
    <vp-chart-tooltip 
      ref="tooltipRef"
      :item="tooltipInfo" 
      :options="tooltipOptions"
      :styles="tooltipStyles"
    >
      <template #header="{ header }">
        <slot name="tooltip:header" :text="header.text" :date="header.date" :rawTime="header.rawTime" />
      </template>
      <template #content="{ headers, lines }">
        <slot name="tooltip:content" :headers="headers" :lines="lines" />
      </template>
    </vp-chart-tooltip>
    <div 
      v-if="!hasData"
      class="vcl-histogram__empty-state"
    >
      <slot name="no-data-message">
        <div class="empty-state-container">
          <h3>{{ $t('noData') }}</h3>
        </div>
      </slot>
    </div>
    <figcaption 
      v-if="enableGraphDescription && hasData"
      class="vcl-histogram__graph-caption"
    >
      <a 
        class="vcl-histogram__description-btn"
        tabindex="0"
        @click="openDescription"
        @keydown.enter="openDescription"
      >
        {{ $t('graphDescription') }}
      </a>
    </figcaption>
  </figure>
</template>

<script>
import moment from 'moment';
import { es, enAU, enCA, enUS } from 'date-fns/locale';
import { Bar } from 'vue-chartjs/legacy';

import VpChartLegends from '../vp-chart-legends/VpChartLegends.vue';
import VpChartTooltip from '../vp-chart-tooltip/VpChartTooltip.vue';
import VpChartDescriptionModal from '../vp-chart-description-modal/VpChartDescriptionModal.vue';

const DEFAULT_COLORS = ['#74BAE1', '#F58220', '#5BBFBD', '#687FD1', '#C66C87', '#016699'];

const LOCALES = {
  es,
  'en-us': enUS,
  'en-au': enAU,
  'en-ca': enCA,
};

export default {
  name: 'VpHistogram',
  components: {
    Bar,
    VpChartLegends,
    VpChartTooltip,
    VpChartDescriptionModal,
  },
  inject: ['$modal'],
  props: {
    labels: {
      type: Array,
      default() {
        return [];
      },
    },
    data: {
      type: Array,
      default() {
        return [];
      },
    },
    options: {
      type: Object,
      default() {
        return {};
      },
    },
    styles: {
      type: Object,
      default() {
        return {};
      },
    }
  },
  emits: ['update', 'export'],
  data() {
    return {
      chartId: null,
      chartInstance: null,
      tooltipInfo: {},
      refresher: null,
    };
  },
  created() {
    this.chartId = `histogram-${this._uid}`
  },
  destroyed() {
    clearInterval(this.refresher);
  },
  watch: {
    'chartData.datasets': {
      immediate: true,
      handler() {
        // Refresher setup
        if (this.refresher) {
          clearTimeout(this.refresher);
        }

        this.refresher = this.setRefresher();
      },
    },
  },
  computed: {
    chartStyles() {
      const { height, width, padding, graphDescription = {}, emptyState = {} } = this.styles;

      const styles = {
        height: height || '100%',
        position: 'relative',
        width: width || '100%',
        padding: padding || '0',
        '--graph-height': 'calc(100% - 60px)',
        '--graph-description-bottom': graphDescription.bottom || '-20px',
        '--graph-description-right': graphDescription.right || '24px',
        '--graph-description-font-size': graphDescription.fontSize || '12px',
        '--graph-description-font-color': graphDescription.fontColor || '#4F4F4F',
        '--graph-description-text-decoration': graphDescription.textDecoration || 'underline',
        '--graph-empty-state-background-color': emptyState.backgroundColor || '#FDFDFD',
        '--graph-empty-state-border': emptyState.border || '1px solid #4F4F4F',
        '--graph-empty-state-font-size': emptyState.fontSize || '32px',
        '--graph-empty-state-font-color': emptyState.fontColor || '#4F4F4F',
        '--graph-empty-state-font-weight': emptyState.fontWeight || '400',
        '--graph-empty-state-margin': emptyState.margin || '3rem 3.125rem 2.5rem',
        '--graph-empty-state-graph-height': emptyState.graphHeight || '70%',
      };

      return styles;
    },
    showLegend() {
      const { showLegend = true } = this.options;
      return showLegend;
    },
    enableGraphDescription() {
      const { graphDescription = {} } = this.options;
      return graphDescription.display;
    },
    hasData() {
      return this.data.some(({ data }) => data && data.length);
    },
    chartData() {
      const visibleIndexes = this.data.reduce((result, { showLine = true }, index) =>  showLine ? result.concat(index) : result, []);

      const _datasets = this.parseData(this.data)
        .map((sets, index) => {
          // Handle index to keep the index under 6 items
          const setsIndex = visibleIndexes.findIndex(i => i === index);
          const boundIndex = setsIndex !== -1 ? setsIndex % 6 : 5;
        
          return {
            type: 'bar',
            hidden: false,
            data: [],
            unit: null,
            yAxisID: 'yAxis1',
            borderRadius: 4,
            backgroundColor: sets.color || DEFAULT_COLORS[boundIndex],
            borderColor: sets.color || DEFAULT_COLORS[boundIndex],
            pointBackgroundColor: sets.color || DEFAULT_COLORS[boundIndex],
            borderWidth: 0,
            borderSkipped: 'bottom',
            label: null,
            borderJoinStyle: 'round',
            ...sets,
          };
        });

      return { datasets: _datasets };
    },
    chartOptions() {
      const {
        indexAxis = 'x',
        timeRange = 'day',
        locale = 'en-us',
        spanGaps = false,
        animation = false,
        tooltip: tooltipOptions = {},
      } = this.options;

      const { maxTicksLimit = 24 } = this.getTimeRangeOptions(timeRange);

      // START: SCALES
      const xAxis = {
        display: true,
        stacked: false,
        ticks: {
          display: true,
          source: 'auto',
          color: '#4F4F4F',
          font: {
            size: 12,
            family: '\'Mulish\',  Helvetica, Arial, sans-serif',
            style: 'normal',
          },
          maxTicksLimit,
          maxRotation: 0,
          ...this.getOptions('xAxis', 'ticks'),
        },
        title: {
          display: false,
          text: 'Time',
          color: '#4F4F4F',
          font: {
            size: 11,
            family: '\'Mulish\',  Helvetica, Arial, sans-serif',
            style: 'normal',
          },
          ...this.getOptions('xAxis', 'title'),
        },
        grid: {
          borderColor: '#4F4F4F',
          borderWidth: 1,
          tickLength: 5,
          ...this.getOptions('xAxis', 'grid'),
        },
        adapters: {
          date: {
            locale: LOCALES[locale] || enUS,
          },
        },
        ...this.getOptions('xAxis', 'override'),
      };
      const yAxis1 = {
        display: true,
        position: 'left',
        min: this.getOptions('yAxis1').min || null,
        max: this.getOptions('yAxis1').max || null,
        beginAtZero: true,
        stacked: true,
        ticks: {
          color: '#4F4F4F',
          font: {
            size: 10,
            family: '\'Mulish\',  Helvetica, Arial, sans-serif',
            style: 'normal',
          },
          ...this.getOptions('yAxis1', 'ticks'),
        },
        title: {
          display: false,
          text: 'Axis 1',
          color: '#4F4F4F',
          font: {
            size: 11,
            family: '\'Mulish\',  Helvetica, Arial, sans-serif',
            style: 'normal',
          },
          padding: {
            bottom: 3,
          },
          ...this.getOptions('yAxis1', 'title'),
        },
        grid: {
          borderColor: '#4F4F4F',
          borderWidth: 1,
          tickLength: 5,
          tickColor: 'transparent',
          ...this.getOptions('yAxis1', 'grid'),
        },
        ...this.getOptions('yAxis1', 'override'),
      };
      const yAxis2 = {
        display: this.data.some(({ yAxisID = 'yAxis1' }) => yAxisID === 'yAxis2'),
        position: 'right',
        min: this.getOptions('yAxis2').min || null,
        max: this.getOptions('yAxis2').max || null,
        beginAtZero: true,
        ticks: {
          color: '#4F4F4F',
          font: {
            size: 10,
            family: '\'Mulish\',  Helvetica, Arial, sans-serif',
            style: 'normal',
          },
          ...this.getOptions('yAxis2', 'ticks'),
        },
        title: {
          display: false,
          text: 'Axis 2',
          color: '#4F4F4F',
          font: {
            size: 11,
            family: '\'Mulish\',  Helvetica, Arial, sans-serif',
            style: 'normal',
          },
          padding: {
            bottom: 3,
          },
          ...this.getOptions('yAxis2', 'title'),
        },
        grid: {
          borderColor: '#4F4F4F',
          borderWidth: 1,
          tickLength: 5,
          tickColor: 'transparent',
          ...this.getOptions('yAxis2', 'grid'),
        },
        ...this.getOptions('yAxis2', 'override'),
      };
      // END: SCALES

      // START: TOOLTIP
      const tooltip = {
        enabled: false,
        intersect: true,
        position: 'cursor',
        usePointStyle: false,
        callbacks: {
          title(tooltipItem) {
            const { title = null } = this.options.tooltip || {};
            const { label: time, raw } = tooltipItem[0];
            const { x: rawTime } = raw;
            const date = moment(rawTime).format('LL');

            return { title, time, date, rawTime };
          },
          label(chart) {
            const { formattedValue: value, dataset, raw } = chart;
            const { tooltipValue = null } = raw || {};
            const { fill: isArea = false } = dataset;

            return { ...dataset, data: null, value: tooltipValue || value, isArea };
          },
        },
        external: (context) => {
          const { chart, tooltip: tooltipModel } = context;
          const { title = [], body, labelColors } = tooltipModel;
          const { canvas } = chart;
          const { headers = [] } = this.options.tooltip || {};

          let tooltipEl = canvas.parentNode.querySelector('div');
          if (!tooltipEl) {
            tooltipEl = document.createElement('div');
            tooltipEl.id = 'chartjs-tooltip';
            canvas.parentNode.appendChild(tooltipEl);
          }
          // Set tooltip opacity
          if (tooltipModel.opacity === 0) {
            tooltipEl.style.opacity = 0;
            // removes the tooltip after transitioning between having and not having data
            // without this the tooltip shows at the bottom of the plot.
            this.tooltipInfo = {};
            return;
          }

          let lines = [];
          if (body) {
            lines = body.reduce((result, bodyItem, i) => {
              const color = labelColors[i];
              return result.concat({ ...bodyItem.lines[0], color });
            }, []);
          }

          // Set Content
          tooltipEl.innerHTML = ''; // Clear contents

          const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;
          const { left = 0, top = 0 } = tooltipOptions.offset || {};
          tooltipEl.style.opacity = 1;
          tooltipEl.style.left = `${positionX + tooltipModel.caretX + left}px`;
          tooltipEl.style.top = `${positionY + tooltipModel.caretY + top}px`;

          this.tooltipInfo = { ...title[0], headers, lines, options: tooltipOptions || {} };
          tooltipEl.appendChild(this.$refs.tooltipRef.$el);
        },
      };
      // END: TOOLTIP
      
      return {
        responsive: true,
        maintainAspectRatio: false,
        spanGaps,
        indexAxis,
        animation,
        scales: {
          x: xAxis,
          yAxis1,
          yAxis2,
        },
        plugins: {
          tooltip,
          legend: {
            display: false,
          },
          chartArea: {
            backgroundColor: '#FDFDFD',
          },
        },
        interaction: {
          intersect: true,
        },
      };
    },
    tooltipOptions() {
      return  this.options.tooltip || {};
    },
    tooltipStyles() {
       const styles = {
        arrow: { display: true },
      };
      return { ...styles, ...(this.styles.tooltip || {})}
    },
    legendStyles() {
      return this.styles.legends || {};
    },
  },
  methods: {
    setChartInstance() {
      const { title } = this.options;
      this.chartInstance = this.$refs.histogramRef;
      this.chartInstance.$refs.canvas.setAttribute('role', 'img');
      this.chartInstance.$refs.canvas.setAttribute('aria-label', title ? `${title} Graph` : 'Histogram');
    },
    parseData(datasets = []) {
      const { parse = false } = this.options;
      if (!parse) return datasets;

      return datasets.map((sets) => {
        let { data = [] } = sets;

        const _data = [];
        for (let i = 0; i < data.length; i += 1) {
          // Format datapoints
          _data.push({ x: moment(data[i].time).valueOf(), y: data[i].value, tooltipValue: data[i].tooltipValue || null });
        }

        return { ...sets, data: _data };
      });
    },
    getTimeRangeOptions(timeRange) {
      const { start, end } = this.options;
      
      if (!start && !end) return {};
      
      const rangeOptions = {
        day: {
          unit: 'hour',
          maxTicksLimit: 24,
          tooltipFormat: 'h:mm A',
        },
        week: {
          unit: 'week',
          maxTicksLimit: 14,
          tooltipFormat: 'MMM D h:mm A',
        },
        month: {
          unit: 'month',
          maxTicksLimit: 30,
          tooltipFormat: 'MMM D h:mm A',
        },
      };

      if (timeRange === 'custom') {
        const daysDiff = moment(end).diff(moment(start), 'days');
        if (daysDiff > 1 && daysDiff < 30) return rangeOptions.week;
        else if (daysDiff >= 30) return rangeOptions.month
        else return rangeOptions.day;
      }
      
      return rangeOptions[timeRange] || {};
    },
    getOptions(field, child = null) {
      const property = this.options[field] || null;

      if (!property) return {};
      if (!child) return property;
      
      return property[child] || {};
    },
    setRefresher() {
      const { timeRange, updateInterval = 30000, start, end } = this.options;
      let refreshTime = updateInterval;
      if (timeRange === 'week') refreshTime *= 7;
      if (timeRange === 'month') refreshTime *= 14;

      return setTimeout(() => this.$emit('update', { start, end }), refreshTime);
    },
    openDescription() {
      return this.$modal.open(VpChartDescriptionModal, { chartData: this.chartData, chartOptions: this.options });
    },
  },
};
</script>

<style lang="scss" scoped>
  .vcl-histogram {
   $self: &;
  position: relative;
  margin: 0;
  padding: 0;
  width: 100%;
  height: 100%;
  &__graph {
    position: relative;
    margin: 0;
    padding: 0;
    width: 100%;
    height: var(--graph-height);
  }
  &__graph-caption {
    padding: 0;
    margin: 0;
    position: absolute;
    bottom: var(--graph-description-bottom);
    right: var(--graph-description-right);
    #{ $self }__description-btn {
      padding: 0;
      margin: 0;
      font-size: var(--graph-description-font-size);
      color: var(--graph-description-font-color);
      text-decoration: var(--graph-description-text-decoration);
      cursor: pointer;
    }
  }
  &__empty-state {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    width: 100%;
    .empty-state-container {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0;
      background-color: var(--graph-empty-state-background-color);
      border: var(--graph-empty-state-border);
      margin: var(--graph-empty-state-margin);
      border-top: none;
      height: var(--graph-empty-state-graph-height);;
      h3 {
        font-size: var(--graph-empty-state-font-size);
        color: var(--graph-empty-state-font-color);
        font-weight: var(--graph-empty-state-font-weight);
      }
    }
  }
  }
</style>
